'use client';

import { useEffect, useRef, useState } from 'react';
import { useSWRConfig } from 'swr';

const THRESHOLD = 70; // px the user must pull before a release triggers a refresh
const MAX_PULL = 110; // cap on how far the indicator travels
const RESISTANCE = 0.5; // makes the drag feel weighted instead of 1:1

/**
 * Pull-to-refresh for the native app / small touch screens.
 *
 * When the page is scrolled to the very top and the user drags down, a circular
 * spinner is revealed under the navbar. Releasing past THRESHOLD triggers a SOFT
 * refresh — it revalidates all SWR data in place rather than reloading the
 * WebView, so there's no blank/black flash. The spinner spins until the
 * revalidation settles (with a small minimum so it doesn't just blink).
 *
 * Disabled on desktop and non-touch devices so it never interferes with normal
 * mouse scrolling. Mounted once globally in Layout, so it covers every page.
 */
const MIN_SPIN_MS = 600; // keep the spinner visible long enough to feel intentional

export default function MobilePullToRefresh() {
  const { mutate } = useSWRConfig();
  const [enabled, setEnabled] = useState(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const startY = useRef<number | null>(null);
  const active = useRef(false);
  const pullRef = useRef(0);

  // Only enable on small, coarse-pointer (touch) devices.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    const isSmall = window.matchMedia('(max-width: 1023px)').matches;
    setEnabled(isTouch && isSmall);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const atTop = () => (document.scrollingElement?.scrollTop ?? window.scrollY) <= 0;

    const setDistance = (d: number) => {
      pullRef.current = d;
      setPull(d);
    };

    const onStart = (e: TouchEvent) => {
      if (refreshing || !atTop()) {
        startY.current = null;
        return;
      }
      startY.current = e.touches[0].clientY;
      active.current = false;
    };

    const onMove = (e: TouchEvent) => {
      if (refreshing || startY.current === null) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta <= 0 || !atTop()) {
        if (active.current) setDistance(0);
        active.current = false;
        return;
      }
      active.current = true;
      setDistance(Math.min(delta * RESISTANCE, MAX_PULL));
      // stop the page from rubber-band scrolling while we own the gesture
      if (e.cancelable) e.preventDefault();
    };

    const onEnd = () => {
      if (refreshing) return;
      if (active.current && pullRef.current >= THRESHOLD) {
        setRefreshing(true);
        setDistance(THRESHOLD);
        // Soft refresh: revalidate every SWR key in place (no page reload).
        const minSpin = new Promise((resolve) => setTimeout(resolve, MIN_SPIN_MS));
        Promise.all([mutate(() => true), minSpin]).finally(() => {
          setRefreshing(false);
          setDistance(0);
        });
      } else {
        setDistance(0);
      }
      startY.current = null;
      active.current = false;
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled, refreshing, mutate]);

  if (!enabled || (pull <= 0 && !refreshing)) return null;

  const progress = Math.min(pull / THRESHOLD, 1);

  return (
    <div
      aria-hidden={!refreshing}
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center"
      style={{
        transform: `translateY(${(refreshing ? THRESHOLD : pull) - 8}px)`,
        transition: active.current ? 'none' : 'transform 0.2s ease',
      }}
    >
      <div className="mt-[var(--safe-top)] flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/5">
        <svg
          className={`h-5 w-5 text-therablue ${refreshing ? 'animate-spin' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          style={refreshing ? undefined : { transform: `rotate(${progress * 270}deg)` }}
        >
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
          <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
}
